import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import {
  getPrivacyApi,
  deletePrivacyApi,
  addOrUpdatePrivacyApi
} from "../../../../services/Web/webPrivacy.service"

import { toastError, toastSuccess } from "../../../../utility/toastutill"

export const fetchPrivacy = createAsyncThunk(
  "privacy/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPrivacyApi()
      console.log('Fetched Privacy Response:', response)
      return response.data
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch privacy policy"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error?.response?.status
      })
    }
  }
)

export const savePrivacy = createAsyncThunk(
  "privacy/save",
  async (privacyData, { rejectWithValue }) => {
    try {
      const response = await addOrUpdatePrivacyApi(privacyData)
      toastSuccess(response.data?.message || "Privacy saved successfully")
      return response.data
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Failed to save privacy policy"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error?.response?.status
      })
    }
  }
)

export const deletePrivacy = createAsyncThunk(
  "privacy/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deletePrivacyApi(id)
      toastSuccess(response.data?.message || "Privacy deleted successfully")
      return { id, ...response.data }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Failed to delete privacy policy"
      toastError(errorMessage)
      return rejectWithValue({
        message: errorMessage,
        status: error?.response?.status
      })
    }
  }
)

const initialState = {
  privacy: [],
  selectedPrivacy: null,
  loading: false,
  error: null,
  total: 0,
  lastUpdated: null,

  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  },

  filters: {
    search: "",
    type: "",
    isActive: true
  }
}

export const privacySlice = createSlice({
  name: "privacy",
  initialState,
  reducers: {
    setPrivacyFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload
      }
    },
    clearPrivacyFilters: (state) => {
      state.filters = {
        search: "",
        type: "",
        isActive: true
      }
    },
    setSelectedPrivacy: (state, action) => {
      state.selectedPrivacy = action.payload
    },
    clearSelectedPrivacy: (state) => {
      state.selectedPrivacy = null
    },
    resetPrivacyState: (state) => {
      state.privacy = []
      state.selectedPrivacy = null
      state.loading = false
      state.error = null
      state.total = 0
      state.lastUpdated = null
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
      }
      state.filters = {
        search: "",
        type: "",
        isActive: true
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPrivacy.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPrivacy.fulfilled, (state, action) => {
        state.loading = false

        const payload = action.payload

        if (Array.isArray(payload)) {
          state.privacy = payload
        } else if (payload && Array.isArray(payload.data)) {
          state.privacy = payload.data
        } else if (payload && payload.data?.privacy && Array.isArray(payload.data.privacy)) {
          state.privacy = payload.data.privacy
        } else if (payload && Array.isArray(payload.privacy)) {
          state.privacy = payload.privacy
        } else {
          state.privacy = []
        }

        if (payload && payload.pagination) {
          state.pagination = {
            page: payload.pagination.page || 1,
            limit: payload.pagination.limit || 10,
            total: payload.pagination.total || state.privacy.length,
            pages: payload.pagination.pages || 0
          }
        } else {
          state.pagination = {
            ...state.pagination,
            total: state.privacy.length,
            pages: Math.ceil(state.privacy.length / state.pagination.limit) || 0
          }
        }

        state.total = state.privacy.length
        state.lastUpdated = new Date().toISOString()
        state.error = null
      })
      .addCase(fetchPrivacy.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastUpdated = new Date().toISOString()
      })

      .addCase(savePrivacy.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(savePrivacy.fulfilled, (state, action) => {
        state.loading = false

        const savedPrivacy = action.payload?.data || action.payload

        if (savedPrivacy) {
          const existingIndex = state.privacy.findIndex(item => item._id === savedPrivacy._id)

          if (existingIndex !== -1) {
            state.privacy[existingIndex] = savedPrivacy
          } else {
            state.privacy.unshift(savedPrivacy)
            state.pagination.total = (state.pagination.total || 0) + 1
          }

          if (state.selectedPrivacy && state.selectedPrivacy._id === savedPrivacy._id) {
            state.selectedPrivacy = savedPrivacy
          }
        }

        state.total = state.privacy.length
        state.pagination.pages = Math.ceil(state.pagination.total / state.pagination.limit) || 0

        state.lastUpdated = new Date().toISOString()
        state.error = null
      })
      .addCase(savePrivacy.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastUpdated = new Date().toISOString()
      })

      .addCase(deletePrivacy.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deletePrivacy.fulfilled, (state, action) => {
        state.loading = false

        const deletedId = action.payload?.id || action.meta?.arg

        if (deletedId) {
          state.privacy = state.privacy.filter(term => term._id !== deletedId)

          if (state.selectedPrivacy && state.selectedPrivacy._id === deletedId) {
            state.selectedPrivacy = null
          }

          state.pagination.total = Math.max((state.pagination.total || 1) - 1, 0)
        }

        state.total = state.privacy.length
        state.pagination.pages = Math.ceil(state.pagination.total / state.pagination.limit) || 0

        state.lastUpdated = new Date().toISOString()
        state.error = null
      })
      .addCase(deletePrivacy.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.lastUpdated = new Date().toISOString()
      })
  }
})

export const {
  setPrivacyFilters,
  clearPrivacyFilters,
  setSelectedPrivacy,
  clearSelectedPrivacy,
  resetPrivacyState
} = privacySlice.actions

export const selectPrivacy = (state) => state.privacy.privacy
export const selectPrivacyLoading = (state) => state.privacy.loading
export const selectPrivacyError = (state) => state.privacy.error
export const selectPrivacyTotal = (state) => state.privacy.total
export const selectSelectedPrivacy = (state) => state.privacy.selectedPrivacy
export const selectPrivacyFilters = (state) => state.privacy.filters
export const selectPrivacyPagination = (state) => state.privacy.pagination

export default privacySlice.reducer